// src/components/widgets/section-header.tsx
import styled from 'styled-components';

type Position = 'left' | 'center' | 'right';
type Variant = 'solid' | 'line';
type HeadingLevel = 'h1' | 'h2' | 'h3' | 'div';

type Props = {
  title: string;
  position?: Position;
  showViewMore?: boolean;
  viewMoreLink?: string;
  viewMoreText?: string;
  variant?: Variant;
  lineColor?: string;
  bgColor?: string;
  titleColor?: string;
  as?: HeadingLevel; //  เพิ่ม prop สำหรับเลือก heading level
};

const WidgetHeader = ({
  title,
  position = 'left',
  showViewMore = true,
  viewMoreLink = '',
  viewMoreText = 'View More',
  variant = 'solid',
  lineColor = '#000',
  bgColor = '#ed1a3b',
  titleColor = '#fff',
  as = 'div' //  default เป็น h1
}: Props) => {
  //  สร้าง dynamic heading component
  const Heading = as;

  return (
    <HeaderWrapper className={`section-header position-${position} variant-${variant}`} $lineColor={lineColor} $bgColor={bgColor} $titleColor={titleColor}>
      {variant === 'line' && <div className='full-width-line' />}

      <div className='container'>
        <div className='header-content'>
          <a href={viewMoreLink} className='title-link'>
            <Heading className='title'>{title}</Heading>
          </a>
          {showViewMore && (
            <a href={viewMoreLink} className='view-more' aria-label={`ดูเพิ่มเติม ${title}`}>
              {viewMoreText}
            </a>
          )}
        </div>
      </div>
    </HeaderWrapper>
  );
};

interface StyledProps {
  $lineColor: string;
  $bgColor: string;
  $titleColor: string;
}

const HeaderWrapper = styled.div<StyledProps>`
  width: 100%;
  position: relative;

  .header-container {
    max-width: 1200px; /*  ขนาด container */
    margin: 0 auto;
    padding: 0 15px;
  }

  .title-link {
    text-decoration: none;
  }

  /* ========== Variant: Solid (Background สี) ========== */
  &.variant-solid {
    background-color: ${({ $bgColor }) => $bgColor};
    padding: 8px 0;

    .header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .title {
      color: ${({ $titleColor }) => $titleColor};
      font-size: 24px;
      font-weight: 700;
      font-style: italic;
      text-transform: uppercase;
      margin: 0;
      flex: 1;
      cursor: pointer;

      @media (max-width: 768px) {
        font-size: 18px;
      }
    }

    .view-more {
      color: ${({ $titleColor }) => $titleColor};
      font-size: 14px;
      font-weight: 400;
      text-decoration: underline;
      white-space: nowrap;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 0.8;
      }
    }

    /* Position */
    &.position-left .title {
      text-align: left;
    }

    &.position-center {
      .header-content {
        justify-content: center;
        position: relative;
      }
      .title {
        text-align: center;
      }
      .view-more {
        position: absolute;
        right: 15px;
      }
    }

    &.position-right {
      .header-content {
        flex-direction: row-reverse;
      }
      .title {
        text-align: right;
      }
    }
  }

  /* ========== Variant: Line (เส้นคาด) ========== */
  &.variant-line {
    padding: 10px 0;

    /*  เส้นเต็มจอ */
    .full-width-line {
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      width: 100vw;
      margin-left: calc(-50vw + 50%);
      height: 2px;
      background-color: ${({ $lineColor }) => $lineColor};
      transform: translateY(-50%);
      z-index: 0;
      @media (max-width: 768px) {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 90%; /* ปรับความยาวได้ เช่น 60%, 70%, 500px */
        height: 2px;
        background-color: ${({ $lineColor }) => $lineColor};
        transform: translate(-50%, -50%);
        z-index: 0;
        margin-left: unset;
      }
    }

    .header-content {
      display: flex;
      align-items: center;
      gap: 20px;
      position: relative;
      z-index: 1;
    }

    .title-link {
      background-color: #fff; /*  พื้นหลังบังเส้น */
      padding: 0 20px 0 0;
    }

    .title {
      color: ${({ $titleColor }) => $titleColor};
      font-size: 60px;
      font-weight: 700;
      text-transform: uppercase;
      margin: 0;
      white-space: nowrap;
      cursor: pointer;

      @media (max-width: 768px) {
        font-size: 36px;
      }
    }

    .view-more {
      color: ${({ $lineColor }) => $lineColor};
      font-size: 16px;
      font-weight: 400;
      text-decoration: underline;
      white-space: nowrap;
      transition: opacity 0.2s ease;
      background-color: #fff; /*  พื้นหลังบังเส้น */
      padding: 0 10px;

      /* &:hover {
        opacity: 0.7;
      } */
    }

    /* Position: Left - title ซ้าย */
    &.position-left {
      .header-content {
        justify-content: space-between;
      }

      .title-link {
        padding: 0 20px 0 0;
      }
    }

    /* Position: Center - title กลาง */
    &.position-center {
      .header-content {
        justify-content: center;
        position: relative;
      }

      .title-link {
        padding: 0 20px;
      }

      .view-more {
        position: absolute;
        right: 0;
      }
    }

    /* Position: Right - title ขวา */
    &.position-right {
      .header-content {
        justify-content: flex-end;
      }

      .title-link {
        padding: 0 0 0 20px;
      }
    }
  }

  @media (max-width: 768px) {
    .view-more {
      font-size: 12px;
    }
  }
`;

export default WidgetHeader;
